import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {Box, FormControlLabel, Radio, RadioGroup, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {requestApi} from "@/boxhub-api";
import {RecipientCandidateSort, RecipientSelectionDTO, RequestRecipientCandidateDTO} from "@/api/data-contracts";
import {CommonI18N, RequestI18N} from "@/translations";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {useSnackbarEx} from "@/lib/snackbarex";
import RecipientCandidateList from "@/common/request-details/RecipientCandidateList";
import {
    applyBulkSelection,
    buildRecipientSelection,
    countHiddenSelected,
    isPublishEnabled,
    RecipientSelectionMode,
    toggleRecipient
} from "@/common/request-details/recipient-selection-logic";

const SEARCH_DEBOUNCE_MS = 250;
const PAGEABLE = { page: 0, size: 600 };

interface RecipientSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (selection: RecipientSelectionDTO) => void;
    deliveryAddressId?: number;
}

/**
 * The "Empfänger der Anfrage festlegen" dialog: two mutually exclusive choices ("Alle zugelassenen
 * Unternehmen" / "Bestimmte Unternehmen"), and, once the second is picked, a searchable, sortable
 * candidate list with a bulk-select action and a hidden-selection hint.
 *
 * `onConfirm` receives the selection built by {@link buildRecipientSelection} and is responsible for
 * attaching it to the actual publish call; the dialog itself never talks to the publish endpoint. The
 * selection lives only in this dialog instance's state: a fresh instance is created every time the
 * caller re-opens it via `showDialog`, so nothing survives between openings once it is closed without
 * confirming.
 */
const RecipientSelectionDialog: React.FC<RecipientSelectionDialogProps> = ({
    open,
    onClose,
    onConfirm,
    deliveryAddressId
}) => {
    const { t } = useTranslation();
    const { showMessage } = useSnackbarEx();

    const [mode, setMode] = useState<RecipientSelectionMode>("ALL");
    const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
    const [candidates, setCandidates] = useState<RequestRecipientCandidateDTO[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const totalElementsKnownRef = useRef(false);
    const [sort, setSort] = useState<RecipientCandidateSort>(RecipientCandidateSort.NAME_ASC);
    const [nameInput, setNameInput] = useState("");
    const [nameFilter, setNameFilter] = useState("");
    const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // Guards against a stale response overwriting a newer one: two quick sort clicks, or a sort click
    // shortly after the search debounce fires, can let an earlier, slower request resolve after a later,
    // faster one. Only the response whose id still matches the latest started request is applied.
    const latestRequestIdRef = useRef(0);

    const loadCandidates = useCallback(async () => {
        latestRequestIdRef.current += 1;
        const requestId = latestRequestIdRef.current;
        try {
            const res = await requestApi.getRecipientCandidates({
                deliveryAddressId,
                name: nameFilter || undefined,
                sortBy: sort,
                pageable: PAGEABLE
            });
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            const data = res.data;
            setCandidates(data.candidates ?? []);
            if (!totalElementsKnownRef.current && !nameFilter) {
                totalElementsKnownRef.current = true;
                setTotalElements(data.totalElements ?? 0);
            }
        } catch (error: unknown) {
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            showMessage({
                summary: t(RequestI18N.details.recipientSelection.loadFailed),
                severity: "error",
                error
            });
        }
    }, [deliveryAddressId, nameFilter, sort, showMessage, t]);

    useEffect(() => {
        if (mode === "SELECTED") {
            loadCandidates();
        }
    }, [mode, loadCandidates]);

    useEffect(
        () => () => {
            if (nameDebounceRef.current !== undefined) {
                clearTimeout(nameDebounceRef.current);
            }
        },
        []
    );

    const handleSearchChange = (value: string) => {
        setNameInput(value);
        if (nameDebounceRef.current !== undefined) {
            clearTimeout(nameDebounceRef.current);
        }
        nameDebounceRef.current = setTimeout(() => setNameFilter(value), SEARCH_DEBOUNCE_MS);
    };

    const displayedCompanyIds = candidates.map((c) => c.companyId).filter((id): id is number => id != null);
    const hiddenSelectedCount = countHiddenSelected(selected, displayedCompanyIds);

    const handleToggle = (companyId: number) => setSelected((prev) => toggleRecipient(prev, companyId));
    const handleBulkToggle = () => setSelected((prev) => applyBulkSelection(prev, displayedCompanyIds));

    const publishEnabled = isPublishEnabled(mode, selected);

    return (
        <ConfirmationDialog
            open={open}
            onClose={onClose}
            onConfirm={() => onConfirm(buildRecipientSelection(mode, selected))}
            closeOnConfirm
            title={t(RequestI18N.details.recipientSelection.title)}
            message=""
            confirmButtonText={t(RequestI18N.details.action.publish)}
            cancelButtonText={t(CommonI18N.action.cancel)}
            confirmDisabled={!publishEnabled}
            autoFocusConfirm={false}
            maxWidth="md"
            fullWidth
            dataCy="recipient-selection-dialog"
        >
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as RecipientSelectionMode)}>
                <FormControlLabel
                    value="ALL"
                    control={<Radio />}
                    label={t(RequestI18N.details.recipientSelection.optionAll)}
                />
                <FormControlLabel
                    value="SELECTED"
                    control={<Radio />}
                    label={t(RequestI18N.details.recipientSelection.optionSelected)}
                />
            </RadioGroup>
            {mode === "ALL" && (
                <Typography variant="body2" color="text.secondary">
                    {t(RequestI18N.details.recipientSelection.hintAll)}
                </Typography>
            )}
            {mode === "SELECTED" && (
                <Box sx={{ mt: 1 }}>
                    <StringInputField
                        label={t(RequestI18N.details.recipientSelection.searchLabel)}
                        value={nameInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    <Box sx={{ mt: 1 }}>
                        <RecipientCandidateList
                            candidates={candidates}
                            selected={selected}
                            totalElements={totalElements}
                            sort={sort}
                            onSortChange={setSort}
                            onToggle={handleToggle}
                            onBulkToggle={handleBulkToggle}
                        />
                    </Box>
                    {hiddenSelectedCount > 0 && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            data-cy="recipient-hidden-hint"
                            sx={{ mt: 1 }}
                        >
                            {hiddenSelectedCount === 1
                                ? t(RequestI18N.details.recipientSelection.hiddenSelectedHintOne)
                                : t(RequestI18N.details.recipientSelection.hiddenSelectedHintMany, {
                                      count: hiddenSelectedCount
                                  })}
                        </Typography>
                    )}
                </Box>
            )}
        </ConfirmationDialog>
    );
};

export default RecipientSelectionDialog;
